import type { ChallengeQuestion } from './quick-challenge-questions';
import {
  QUESTIONS,
  COLORS,
  TEST_HEX_COLORS,
} from './quick-challenge-questions';

// Helper function for deterministic array indexing
const deterministicIndex = (base: number, len: number): number =>
  ((base % len) + len) % len;

export interface QuestionWithMeta {
  text: string;
  category: string;
  color: string;
}

export function getRandomChallengeQuestion(seed?: number): string {
  const base = seed ?? Date.now();
  const randomIndex = deterministicIndex(base, QUESTIONS.length);
  return QUESTIONS[randomIndex].text;
}

// Helper function to determine grade level category from profile grade
function getGradeLevelCategory(grade?: string): string | null {
  if (!grade) return null;

  // Map grade levels to categories
  const gradeMap: Record<string, string> = {
    '1A': '1A-1B',
    '1B': '1A-1B',
    '1C': '1C-2A',
    '2A': '1C-2A',
    '2B': '2B-2C',
    '2C': '2B-2C',
    '3A': '3A-3C',
    '3B': '3A-3C',
    '3C': '3A-3C',
  };

  return gradeMap[grade] || null;
}

// Get questions filtered by grade level
export function getQuestionsByGradeLevel(grade?: string): ChallengeQuestion[] {
  if (!grade) return QUESTIONS;

  const gradeCategory = getGradeLevelCategory(grade);
  if (!gradeCategory) return QUESTIONS;

  return QUESTIONS.filter(question => question.grade_level === gradeCategory);
}

// Extract answered questions from chat history
function getAnsweredQuestions(
  chatHistory: Array<{ question?: string }>
): string[] {
  return chatHistory
    .filter(Boolean)
    .map(entry => entry.question)
    .filter((question): question is string => typeof question === 'string')
    .map(question => question.toLowerCase().trim());
}

// Check if a question has already been answered
function isQuestionAlreadyAnswered(
  questionText: string,
  answeredQuestions: string[]
): boolean {
  const normalizedQuestion = questionText.toLowerCase().trim();
  return answeredQuestions.some(
    answered =>
      answered.includes(normalizedQuestion) ||
      normalizedQuestion.includes(answered)
  );
}

export function getRandomChallengeQuestionWithMeta(
  userGrade?: string,
  chatHistory?: Array<{ question?: string }>,
  seed?: number
): QuestionWithMeta {
  const availableQuestions = getQuestionsByGradeLevel(userGrade);
  const answeredQuestions = getAnsweredQuestions(chatHistory ?? []);

  // Filter out questions that have already been answered
  const unansweredQuestions = availableQuestions.filter(
    question => !isQuestionAlreadyAnswered(question.text, answeredQuestions)
  );

  // If all questions have been answered, fall back to all available questions
  const questionsToUse =
    unansweredQuestions.length > 0 ? unansweredQuestions : availableQuestions;

  const base = seed ?? Date.now();
  const randomIndex = deterministicIndex(base, questionsToUse.length);
  const randomColorIndex = deterministicIndex(
    base + 1,
    (process.env.NODE_ENV === 'test' ? TEST_HEX_COLORS : COLORS).length
  );

  const palette = process.env.NODE_ENV === 'test' ? TEST_HEX_COLORS : COLORS;

  return {
    text: questionsToUse[randomIndex].text,
    category: questionsToUse[randomIndex].category,
    color: palette[randomColorIndex],
  };
}

// Get a specific question by ID
export function getQuestionById(id: string): ChallengeQuestion | undefined {
  return QUESTIONS.find(question => question.id === id);
}

// Get questions by category
export function getQuestionsByCategory(category: string): ChallengeQuestion[] {
  return QUESTIONS.filter(question => question.category === category);
}

// Get all available categories
export function getAllCategories(): string[] {
  const categories = new Set(QUESTIONS.map(question => question.category));
  return [...categories];
}

// Get all available grade levels
export function getAllGradeLevels(): string[] {
  const gradeLevels = new Set(
    QUESTIONS.map(question => question.grade_level).filter(
      (level): level is string => typeof level === 'string'
    )
  );
  return [...gradeLevels];
}

// Local storage keys aligned with tests
const FIRST_LOGIN_KEY = 'first_login_date';
const TRIGGERS_KEY = 'quick_challenge_login_triggers';
const OFFLINE_SHOWN_TODAY_KEY = 'offline_quick_challenge_shown_today';

interface LoginTriggersState {
  triggered: number[];
}

// Check and update login triggers with milestone logic
export function checkAndUpdateLoginTriggers(): {
  shouldShow: boolean;
  triggerType?: string;
} {
  try {
    // Get or set first login date
    const now = Date.now();
    const firstLoginDate = localStorage.getItem(FIRST_LOGIN_KEY);
    if (!firstLoginDate) {
      localStorage.setItem(FIRST_LOGIN_KEY, String(now));
      // initialize triggers state
      const initState: LoginTriggersState = { triggered: [] };
      localStorage.setItem(TRIGGERS_KEY, JSON.stringify(initState));
      return { shouldShow: false }; // Do not show on first login
    }

    const daysSinceFirstLogin = Math.floor(
      (now - Number.parseInt(firstLoginDate, 10)) / (1000 * 60 * 60 * 24)
    );

    // Milestones in days expected by tests
    const milestones = [2, 7, 14, 30];

    const rawState = localStorage.getItem(TRIGGERS_KEY);
    const state: LoginTriggersState = rawState
      ? { triggered: JSON.parse(rawState) as number[] }
      : { triggered: [] };

    const milestone = milestones.find(
      d => daysSinceFirstLogin >= d && !state.triggered.includes(d)
    );

    if (milestone !== undefined) {
      state.triggered.push(milestone);
      localStorage.setItem(TRIGGERS_KEY, JSON.stringify(state.triggered));
      return { shouldShow: true, triggerType: `${milestone}-day-milestone` };
    }

    return { shouldShow: false };
  } catch {
    return { shouldShow: false };
  }
}

// Check if QuickChallenge was already shown today for OFFLINE delivery
export function hasShownOfflineQuickChallengeToday(): boolean {
  try {
    const shownToday = localStorage.getItem(OFFLINE_SHOWN_TODAY_KEY);
    if (!shownToday) return false;

    const shownData = JSON.parse(shownToday) as {
      date: string;
      shown: boolean;
    };
    const today = new Date().toDateString();

    // Clean up stale entries automatically
    if (shownData.date !== today) {
      localStorage.removeItem(OFFLINE_SHOWN_TODAY_KEY);
      return false;
    }

    return shownData.date === today && shownData.shown;
  } catch {
    return false;
  }
}

// Mark that QuickChallenge was shown today for OFFLINE delivery
export function markOfflineQuickChallengeShownToday(): void {
  try {
    const today = new Date().toDateString();
    localStorage.setItem(
      OFFLINE_SHOWN_TODAY_KEY,
      JSON.stringify({
        date: today,
        shown: true,
      })
    );
  } catch {
    // Handle localStorage errors gracefully
  }
}

// Reset all login trigger related data
export function resetLoginTriggers(): void {
  try {
    localStorage.removeItem(FIRST_LOGIN_KEY);
    localStorage.removeItem(TRIGGERS_KEY);
    localStorage.removeItem(OFFLINE_SHOWN_TODAY_KEY);
  } catch {
    // ignore
  }
}
